import { searchAssetsSchemas } from '@atlassian/search-client/assets';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { assetsSchemasQuery } from '@atlassian/search-client/schemas';

import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const productKeys1P: ProductKeys1P[] = [ProductKeys.Assets];

export const filter: Readonly<{
	id: 'assetsSchema';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'assetsSchema'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'assetsSchema'>;
	getInitialState: (
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'assetsSchema'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'assetsSchema',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'assetsSchema',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});

			const allSchemas = await assetsSchemasQuery(queryParams, headers);
			const hydratedSchemaOptions = allSchemas.data?.assets_schemasByIds?.map((schema) => {
				return {
					trackingKey: schema.id,
					value: schema.id,
					queryParamValue: schema.id,
					label: schema.name || '',
					product: config.product,
					isSuggested: false,
					isSelected: false,
				};
			});

			return hydratedSchemaOptions ?? [];
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			const suggestedSchemas = await searchAssetsSchemas();
			const recommendedSchemaOptions = suggestedSchemas.map((schema) => {
				return {
					trackingKey: schema.ari,
					value: schema.ari,
					queryParamValue: schema.ari,
					label: schema.name || '',
					product: config.product,
					isSuggested: true,
					isSelected: false,
				};
			});

			return recommendedSchemaOptions;
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			const resultSchemas = await searchAssetsSchemas(query);
			const searchSchemaOptions = resultSchemas.map((schema) => {
				return {
					trackingKey: schema.ari,
					value: schema.ari,
					queryParamValue: schema.ari,
					label: schema.name || '',
					product: config.product,
					isSuggested: false,
					isSelected: false,
				};
			});
			return searchSchemaOptions;
		},
	},
	isSuggestedFilter: (primaryProduct) => primaryProduct === ProductKeys.Assets,
});
